import React from 'react';
import { Footer, GradientHero, SEO, breadcrumbSchema } from '@coupling/ui';
import Header from '../components/Header';
import {
  getFeedCreative,
  merchantLaunchConcepts,
} from '../content/facebookMerchantLaunchBundle';
import { seoConfig } from '../config/seo';

const AdConceptPreview: React.FC = () => {
  return (
    <div className="min-h-screen bg-[#fff8f1]">
      <SEO
        title={`Facebook Campaign Concepts - ${seoConfig.siteName}`}
        description="Preview browser-rendered mockups for the current Coupling WNC Facebook merchant-acquisition concepts."
        canonical={`${seoConfig.siteUrl}/preview/facebook-merchant-acquisition`}
        ogImage={seoConfig.defaultOgImage}
        ogUrl={`${seoConfig.siteUrl}/preview/facebook-merchant-acquisition`}
        jsonLd={breadcrumbSchema([
          { name: 'Home', url: seoConfig.siteUrl },
          { name: 'Facebook Campaign Concepts', url: `${seoConfig.siteUrl}/preview/facebook-merchant-acquisition` },
        ])}
      />
      <Header />

      <GradientHero
        title="Facebook Campaign Concepts"
        description="Review the four merchant-acquisition directions as browser-rendered ad mockups, each paired with its message-matched landing page."
        className="py-16"
      >
        <div className="mx-auto mt-8 max-w-3xl rounded-[1.75rem] border border-white/20 bg-white/10 px-6 py-5 text-left text-white/90 backdrop-blur-sm">
          <p className="text-sm font-semibold uppercase tracking-[0.16em] text-white/70">
            Review Notes
          </p>
          <p className="mt-3 text-base leading-relaxed">
            These are not exported final creatives. They are browser-native mockups to evaluate
            message hierarchy, on-image text, CTA clarity, and landing-page continuity before we commit
            to final static rendering or image generation.
          </p>
        </div>
      </GradientHero>

      <section className="px-4 py-16">
        <div className="mx-auto grid max-w-7xl gap-8 xl:grid-cols-2">
          {merchantLaunchConcepts.map((concept) => {
            const feedCreative = getFeedCreative(concept);

            return (
            <article
              key={concept.slug}
              className="overflow-hidden rounded-[2rem] border border-[#e7d8c9] bg-white shadow-[0_26px_70px_rgba(77,44,25,0.10)]"
            >
              <div className="border-b border-[#f0e3d6] bg-[#fffaf5] px-6 py-5">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="rounded-full bg-[#f6e6d6] px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-[#8e4a28]">
                    {concept.angle}
                  </span>
                  <span className="text-sm font-medium text-neutral-500">
                    {concept.audience}
                  </span>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-neutral-600">
                  {concept.note}
                </p>
              </div>

              <div className="p-6">
                <div className="rounded-[2rem] border border-[#e9ddd2] bg-[#fefbf8] p-4 shadow-inner">
                  <div className="flex items-center gap-3 px-2 pb-4">
                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[linear-gradient(135deg,#cf6438,#f0b348)] text-base font-bold text-white">
                      C
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-neutral-900">Coupling WNC</p>
                      <p className="text-xs text-neutral-500">Sponsored</p>
                    </div>
                  </div>

                  <p className="px-2 text-[0.95rem] leading-relaxed text-neutral-700">
                    {feedCreative.primaryText}
                  </p>

                  <div className="mt-4 overflow-hidden rounded-[1.6rem] border border-[#e8d7c7] bg-white">
                    <div
                      className={`relative min-h-[24rem] bg-gradient-to-br ${concept.accent}`}
                    >
                      {concept.previewStyle === 'framed-device' ? (
                        <>
                          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,249,238,0.22),transparent_28%),linear-gradient(180deg,rgba(83,47,18,0.02),rgba(83,47,18,0.36))]" />
                          <div className="absolute inset-y-3 right-3 w-[40%] overflow-hidden rounded-[2rem] border border-[#f2deca] bg-[linear-gradient(180deg,rgba(255,251,245,0.94),rgba(250,236,216,0.86))] shadow-[0_24px_60px_rgba(92,57,25,0.22)]">
                            <img
                              src={concept.backgroundImage}
                              alt=""
                              className="h-full w-full scale-[1.24] object-contain object-center"
                            />
                          </div>
                        </>
                      ) : concept.previewStyle === 'product-focus' ? (
                        <>
                          <img
                            src={concept.backgroundImage}
                            alt=""
                            className="absolute inset-0 h-full w-full object-cover object-[center_42%]"
                          />
                          <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(41,22,13,0.74)_0%,rgba(41,22,13,0.38)_44%,rgba(41,22,13,0.18)_100%)]" />
                          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,247,236,0.20),transparent_28%)]" />
                        </>
                      ) : (
                        <>
                          <img
                            src={concept.backgroundImage}
                            alt=""
                            className="absolute inset-0 h-full w-full object-cover opacity-28"
                          />
                          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.24),transparent_30%),linear-gradient(180deg,rgba(23,16,11,0.02),rgba(23,16,11,0.48))]" />
                        </>
                      )}

                      <div className="relative flex min-h-[24rem] flex-col justify-between p-6 text-white">
                        <div className="max-w-[18rem] rounded-[1.3rem] border border-white/15 bg-white/10 px-4 py-3 backdrop-blur-sm">
                          <p className="text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-white/75">
                            Coupling WNC
                          </p>
                          <h2 className="mt-3 text-3xl font-bold leading-tight">
                            {feedCreative.headline}
                          </h2>
                          <p className="mt-3 text-sm leading-relaxed text-white/84">
                            {feedCreative.subheadline}
                          </p>
                        </div>

                        <div className="flex items-end justify-between gap-4">
                          <div className="max-w-[13rem] rounded-[1.1rem] border border-white/12 bg-black/15 px-4 py-3 backdrop-blur-sm">
                            <p className="text-xs font-medium uppercase tracking-[0.16em] text-white/70">
                              CTA
                            </p>
                            <p className="mt-2 text-sm font-semibold text-white">
                              {feedCreative.footer}
                            </p>
                          </div>

                          <div className="flex gap-2">
                            <div className="rounded-[1rem] border border-white/12 bg-white/12 px-3 py-2 text-xs font-medium text-white/90 backdrop-blur-sm">
                              4:5
                            </div>
                            <div className="rounded-[1rem] border border-white/12 bg-white/12 px-3 py-2 text-xs font-medium text-white/90 backdrop-blur-sm">
                              Feed
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-4 border-t border-[#eee2d7] bg-[#fffaf5] px-5 py-4">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-neutral-500">
                          Landing page
                        </p>
                        <a
                          href={concept.landingPath}
                          className="mt-1 inline-block text-sm font-semibold text-brand-primary hover:underline"
                        >
                          {concept.landingPath}
                        </a>
                      </div>
                      <a
                        href={concept.landingPath}
                        className="inline-flex items-center justify-center rounded-full bg-[#f0b748] px-5 py-2.5 text-sm font-semibold text-[#47260f] shadow-[0_14px_28px_rgba(240,183,72,0.28)] transition-transform hover:-translate-y-0.5"
                      >
                        Open page
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </article>
            );
          })}
        </div>
      </section>

      <Footer
        description="Browser-rendered concept previews for the current Coupling WNC Facebook merchant-acquisition campaign."
      />
    </div>
  );
};

export default AdConceptPreview;
